/**
 * List paging for the CRM tables. The API returns a plain array and caps it at `limit`, so the
 * client asks for one row more than it shows: if that extra row comes back, there is another page.
 * Without this a list silently stops at the API default (50 rows) with no sign that rows are missing.
 */
export const PAGE_SIZE = 50;
/** Dropdowns and pickers have no "load more": they ask for everything up to the API's ceiling. */
export const PICKER_LIMIT = 1000;

export interface PageOpts {
  limit?: number;
  offset?: number;
}

export function pageQuery(params: URLSearchParams, page?: PageOpts): void {
  if (page?.limit != null) params.set("limit", String(page.limit));
  if (page?.offset) params.set("offset", String(page.offset));
}

export const firstPage = (): PageOpts => ({ limit: PAGE_SIZE + 1, offset: 0 });
export const nextPage = (alreadyLoaded: number): PageOpts => ({ limit: PAGE_SIZE + 1, offset: alreadyLoaded });
export const pickerPage = (): PageOpts => ({ limit: PICKER_LIMIT, offset: 0 });

export function trimPage<T>(rows: T[]): { items: T[]; hasMore: boolean } {
  return { items: rows.slice(0, PAGE_SIZE), hasMore: rows.length > PAGE_SIZE };
}

/** A list response that also names how many rows match, not only this page. */
export interface Counted<T> {
  items: T[];
  total: number;
}

/** Accept either a plain array (older callers and test doubles) or a counted page. */
export function listedRows<T>(value: T[] | Counted<T>): { rows: T[]; total: number | null } {
  if (Array.isArray(value)) return { rows: value, total: null };
  return { rows: value.items, total: value.total };
}

/** Append a fetched page, skipping rows already shown (a row inserted meanwhile shifts the offset). */
export function appendUnique<T extends { id: string }>(current: T[], more: T[]): T[] {
  const seen = new Set(current.map((row) => row.id));
  return [...current, ...more.filter((row) => !seen.has(row.id))];
}
